<script setup lang="ts">
import { ref, reactive, computed, onBeforeMount } from 'vue'
import { useRoute } from 'vue-router'
import { API, HOST } from '@/utils/http'
import { toast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Plus, Play, Trash2, Settings } from 'lucide-vue-next'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'
import type { Skill, ProjectSkill } from '@/types/Skill'

const route = useRoute()
const projectId = Number(route.params.project_id)

const projectSkills = ref<ProjectSkill[]>([])
const catalog = ref<Skill[]>([])
const drafts = reactive<Record<number, number>>({})
const openSettings = ref<number | null>(null)
const runningSkill = ref<number | null>(null)

const confirmRemoveDialog = ref<InstanceType<typeof DialogConfirm> | null>(null)
const pendingRemove = ref<ProjectSkill | null>(null)

const projectSkillsLength = computed(() => projectSkills.value.length)
const addedIds = computed(() => new Set(projectSkills.value.map((ps) => ps.skill_id)))
const isAdded = (skill: Skill) => addedIds.value.has(skill.id)

const fetchAll = async () => {
  try {
    const [added, cat] = await Promise.all([
      API({ method: 'GET', url: `${HOST}/project-skills/${projectId}` }),
      API({ method: 'GET', url: `${HOST}/project-skills/catalog` }),
    ])
    projectSkills.value = added.data
    catalog.value = cat.data
    for (const ps of added.data) drafts[ps.skill_id] = ps.lookback_days
  } catch (e) {
    toast({ title: 'Failed to load skills', variant: 'destructive' })
  }
}

const addSkill = async (skill: Skill) => {
  try {
    await API({
      method: 'PUT',
      url: `${HOST}/project-skills/${projectId}`,
      data: { skill_id: skill.id, enabled: true, lookback_days: skill.default_lookback_days },
    })
    toast({ title: `Added ${skill.name}` })
    await fetchAll()
  } catch (e) {
    toast({ title: 'Failed to add skill', variant: 'destructive' })
  }
}

const saveSettings = async (ps: ProjectSkill) => {
  const lookback = Math.max(Number(drafts[ps.skill_id]) || 1, 1)
  try {
    await API({
      method: 'PUT',
      url: `${HOST}/project-skills/${projectId}`,
      data: { skill_id: ps.skill_id, enabled: ps.enabled, lookback_days: lookback },
    })
    ps.lookback_days = lookback
    drafts[ps.skill_id] = lookback
    openSettings.value = null
    toast({ title: 'Settings saved' })
  } catch (e) {
    toast({ title: 'Failed to save settings', variant: 'destructive' })
  }
}

const runNow = async (ps: ProjectSkill) => {
  runningSkill.value = ps.skill_id
  try {
    const res = await API({ method: 'POST', url: `${HOST}/daily-review/run/${projectId}?skill_id=${ps.skill_id}` })
    if (res.data?.created === false) {
      toast({ title: 'Nothing to review', description: 'No new summarized reports in the window.' })
    } else {
      toast({ title: 'Daily review generated', description: 'Open it from the Reports tab.' })
    }
  } catch (e) {
    toast({ title: 'Failed to run skill', variant: 'destructive' })
  } finally {
    runningSkill.value = null
  }
}

const promptRemove = (ps: ProjectSkill) => {
  pendingRemove.value = ps
  confirmRemoveDialog.value?.open()
}

const removeSkill = async () => {
  if (!pendingRemove.value) return
  try {
    await API({ method: 'DELETE', url: `${HOST}/project-skills/${projectId}/${pendingRemove.value.skill_id}` })
    toast({ title: 'Skill removed' })
    await fetchAll()
  } catch (e) {
    toast({ title: 'Failed to remove skill', variant: 'destructive' })
  } finally {
    pendingRemove.value = null
  }
}

onBeforeMount(fetchAll)
</script>

<template>
  <div>
    <!-- Skills (added to the project) -->
    <div class="mb-3 flex items-center">
      <h3 class="text-base font-bold">Skills ({{ projectSkillsLength }})</h3>
    </div>

    <div v-if="projectSkills.length" class="space-y-2">
      <div
        v-for="ps in projectSkills"
        :key="ps.id"
        class="flex items-center gap-3 rounded-md border p-3"
      >
        <div class="flex-1 min-w-0">
          <div class="text-sm font-medium">{{ ps.skill.name }}</div>
          <div class="text-xs text-muted-foreground truncate">{{ ps.skill.description }}</div>
        </div>
        <span class="text-xs text-muted-foreground">Every {{ ps.lookback_days }}d</span>
        <Popover
          :open="openSettings === ps.skill_id"
          @update:open="(v) => (openSettings = v ? ps.skill_id : null)"
        >
          <PopoverTrigger as-child>
            <Button variant="ghost" size="icon" class="h-8 w-8" title="Settings">
              <Settings class="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent class="w-56">
            <div class="space-y-2">
              <Label>Run every (days)</Label>
              <Input v-model.number="drafts[ps.skill_id]" type="number" min="1" />
              <p class="text-xs text-muted-foreground">
                Runs on this interval and analyzes data from the same lookback
                window (e.g. every 7 days, covering the last 7 days).
              </p>
              <div class="flex justify-end pt-1">
                <Button size="sm" @click="saveSettings(ps)">Save</Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
        <Button
          v-if="ps.skill.skill_type === 'daily_review'"
          type="button"
          variant="outline"
          size="sm"
          :disabled="runningSkill === ps.skill_id"
          @click="runNow(ps)"
        >
          <Play class="h-3.5 w-3.5 mr-1" />
          {{ runningSkill === ps.skill_id ? 'Running…' : 'Run now' }}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          class="h-8 w-8"
          title="Remove skill"
          @click="promptRemove(ps)"
        >
          <Trash2 class="h-4 w-4 text-destructive" />
        </Button>
      </div>
    </div>
    <div v-else class="text-center py-6 text-muted-foreground text-sm">
      No skills added yet. Add one from the library below.
    </div>

    <!-- Skills Library (all global skills; added ones disabled) -->
    <div class="mt-8 mb-3 flex items-center">
      <h3 class="text-base font-bold">Skills Library ({{ catalog.length }})</h3>
    </div>

    <TooltipProvider>
      <div v-if="catalog.length" class="space-y-2">
        <div
          v-for="skill in catalog"
          :key="skill.id"
          class="flex items-center gap-3 rounded-md border p-3"
          :class="{ 'opacity-60': isAdded(skill) }"
        >
          <div class="flex-1 min-w-0">
            <div class="text-sm font-medium">{{ skill.name }}</div>
            <div class="text-xs text-muted-foreground truncate">{{ skill.description }}</div>
          </div>
          <Tooltip v-if="isAdded(skill)">
            <TooltipTrigger as-child>
              <span class="inline-flex">
                <Button type="button" variant="outline" size="sm" disabled>
                  <Plus class="h-3.5 w-3.5 mr-1" />
                  Add
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>Already in project</TooltipContent>
          </Tooltip>
          <Button v-else type="button" variant="outline" size="sm" @click="addSkill(skill)">
            <Plus class="h-3.5 w-3.5 mr-1" />
            Add
          </Button>
        </div>
      </div>
      <div v-else class="text-center py-6 text-muted-foreground text-sm">
        No skills available
      </div>
    </TooltipProvider>

    <DialogConfirm
      ref="confirmRemoveDialog"
      title="Remove skill?"
      description="This removes the skill from the project and returns it to the library. Its configuration is discarded."
      confirm-text="Remove"
      variant="error"
      @confirm="removeSkill"
    />
  </div>
</template>
